// Основной каркас: IconNav + Sidebar + Topbar. Grid 64 / 240 / 1fr.
import { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Building2, Calculator, FolderKanban,
  Library, Settings, LogOut, Search, Bell, Bot, MessagesSquare, Mail, FileCheck, Plug, Shield, CheckSquare, CalendarDays, BookMarked,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import './layout.css';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Дашборд', resource: null as string | null },
  { to: '/contracts', icon: FileText, label: 'Договоры', resource: 'contracts' },
  { to: '/objects', icon: Building2, label: 'Объекты', resource: 'objects' },
  { to: '/estimates', icon: Calculator, label: 'Сметы', resource: 'estimates' },
  { to: '/acts', icon: FileCheck, label: 'Акты', resource: 'acts' },
  { to: '/tasks', icon: CheckSquare, label: 'Задачи', resource: null as string | null },
  { to: '/calendar', icon: CalendarDays, label: 'Календарь', resource: null as string | null },
  { to: '/projects', icon: FolderKanban, label: 'Проекты', resource: 'projects' },
  { to: '/library', icon: Library, label: 'Библиотека', resource: 'library' },
  { to: '/normative', icon: BookMarked, label: 'Нормативы', resource: 'library' },
  { to: '/integrations', icon: Plug, label: 'Интеграции', resource: 'integrations' },
  { to: '/chat', icon: MessagesSquare, label: 'Чат', resource: null as string | null },
  { to: '/mail', icon: Mail, label: 'Почта', resource: null as string | null },
];

function IconNav() {
  const { can, user } = useAuth();
  return (
    <nav className="iconnav">
      <div className="iconnav__items">
        {NAV_ITEMS.filter((i) => i.resource === null || can(i.resource, 'view')).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'}
            className={({ isActive }) => `iconnav__item${isActive ? ' iconnav__item--active' : ''}`}
            title={item.label}>
            <item.icon size={20} />
            <span className="iconnav__tip">{item.label}</span>
          </NavLink>
        ))}
      </div>
      {(user?.companies.some((c) => c.is_admin) || user?.is_platform_admin) && (
        <NavLink to="/admin"
          className={({ isActive }) => `iconnav__item${isActive ? ' iconnav__item--active' : ''}`}
          title="Администрирование">
          <Settings size={20} />
          <span className="iconnav__tip">Администрирование</span>
        </NavLink>
      )}
      {user?.is_platform_admin && (
        <NavLink to="/platform"
          className={({ isActive }) => `iconnav__item${isActive ? ' iconnav__item--active' : ''}`}
          title="Платформа">
          <Shield size={20} />
          <span className="iconnav__tip">Платформа</span>
        </NavLink>
      )}
    </nav>
  );
}

function Topbar() {
  const { user, currentCompany, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="topbar">
      <div className="topbar__logo" onClick={() => navigate('/')}>
        <span className="topbar__logo-mark">S</span>
        <span className="topbar__logo-text">STRUXIS</span>
      </div>
      <div className="topbar__search">
        <Search size={15} />
        <input placeholder="Поиск по договорам, объектам, сметам…" />
        <kbd>⌘K</kbd>
      </div>
      <div className="topbar__right">
        <button className="topbar__icon-btn" title="AI-ассистент"><Bot size={18} /></button>
        <button className="topbar__icon-btn" title="Уведомления"><Bell size={18} /></button>
        <div className="topbar__user">
          <div className="topbar__user-info">
            <span className="topbar__user-name">{user?.short_name}</span>
            <span className="topbar__user-company">{currentCompany?.company_name}</span>
          </div>
          <button className="topbar__icon-btn" title="Выход" onClick={logout}><LogOut size={18} /></button>
        </div>
      </div>
    </header>
  );
}

interface AppLayoutProps { children: ReactNode; sidebar?: ReactNode; }

export function AppLayout({ children, sidebar }: AppLayoutProps) {
  const noSidebar = !sidebar;
  return (
    <div className={`app-shell${noSidebar ? ' app-shell--no-sidebar' : ''}`}>
      <Topbar />
      <IconNav />
      {sidebar && <aside className="sidebar">{sidebar}</aside>}
      <main className="main-content">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-header__title">{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </div>
  );
}

export function SidebarNav({ title, items, activeKey, onSelect }: {
  title: string;
  items: Array<{ key: string; label: string; count?: number }>;
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  const location = useLocation();
  return (
    <div className="sidebar-nav">
      <div className="sidebar-nav__title">{title}</div>
      {items.map((item) => (
        <button key={item.key}
          className={`sidebar-nav__item${item.key === activeKey ? ' sidebar-nav__item--active' : ''}`}
          onClick={() => onSelect(item.key)}>
          <span>{item.label}</span>
          {item.count !== undefined && <span className="sidebar-nav__count">{item.count}</span>}
        </button>
      ))}
      <div className="sidebar-nav__path">{location.pathname}</div>
    </div>
  );
}
